import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Loader2, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const input = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brandorange";
const label = "mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400";
const REGIONS = ["India", "International"];

export default function AdminGallery() {
  const [items, setItems] = useState(null);
  const [dests, setDests] = useState([]);
  const [region, setRegion] = useState("India");
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [addTab, setAddTab] = useState("url");
  const [form, setForm] = useState({ image: "", title: "", destination: "", region: "India" });
  const [ai, setAi] = useState({ prompt: "", count: 6, destination: "", region: "India" });
  const [saving, setSaving] = useState(false);
  const [gen, setGen] = useState(false);
  const poll = useRef(null);

  const load = () => api.get("/admin/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/admin/destinations").then((r) => setDests(r.data || [])).catch(() => {}); return () => poll.current && clearInterval(poll.current); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setA = (k) => (e) => setAi({ ...ai, [k]: e.target.value });

  const uploadFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)"); return; }
    const rd = new FileReader(); rd.onload = () => setForm((f) => ({ ...f, image: rd.result })); rd.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error("Add an image URL or upload a file"); return; }
    setSaving(true);
    try { await api.post("/admin/gallery", { ...form, category: form.destination || "General" }); toast.success("Image added"); setOpen(false); setForm({ image: "", title: "", destination: "", region }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!ai.prompt.trim()) { toast.error("Describe what to generate (e.g. Himachal snow mountains)"); return; }
    setGen(true);
    try {
      const r = await api.post("/admin/gallery/ai-generate", { ...ai, count: Number(ai.count) || 6 });
      toast.success(r.data.message || "Generating…");
      setAiOpen(false);
      const before = (items || []).length;
      let ticks = 0;
      poll.current && clearInterval(poll.current);
      poll.current = setInterval(async () => {
        ticks += 1;
        const res = await api.get("/admin/gallery").catch(() => null);
        if (res) { setItems(res.data); if (res.data.length >= before + (Number(ai.count) || 6) || ticks > 40) { clearInterval(poll.current); toast.success("AI images ready"); } }
      }, 4000);
      setAi({ prompt: "", count: 6, destination: "", region });
    } catch (err) { toast.error(apiError(err)); } finally { setGen(false); }
  };

  const del = async (id) => { if (!window.confirm("Delete image?")) return; await api.delete(`/admin/gallery/${id}`); load(); };

  const shown = (items || []).filter((g) => (g.region || "India") === region);
  const byDest = shown.reduce((acc, g) => { const d = g.destination || g.category || "Other"; (acc[d] = acc[d] || []).push(g); return acc; }, {});
  dests.filter((d) => (d.region || "India") === region).forEach((d) => { if (!byDest[d.name]) byDest[d.name] = []; });

  return (
    <AdminLayout title="Gallery">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {REGIONS.map((r) => <button key={r} onClick={() => setRegion(r)} data-testid={`gallery-region-${r}`} className={`rounded-full px-5 py-2 text-sm font-bold ${region === r ? "brand-gradient text-white" : "border border-gray-200 text-navy"}`}>{r}</button>)}
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setAi((a) => ({ ...a, region })); setAiOpen(true); }} data-testid="ai-generate-btn" className="flex items-center gap-2 rounded-full border-2 border-brandorange px-4 py-2 text-sm font-bold text-brandorange hover:bg-orange-50"><Sparkles className="h-4 w-4" /> AI Generate</button>
          <button onClick={() => { setForm((f) => ({ ...f, region })); setOpen(true); }} data-testid="add-gallery-btn" className="btn-gradient flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold"><Plus className="h-4 w-4" /> Add Image</button>
        </div>
      </div>

      {items === null ? <div className="grid grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-32 skeleton rounded-2xl" />)}</div>
        : Object.keys(byDest).length === 0 ? <div className="rounded-3xl border border-dashed border-gray-200 py-16 text-center text-gray-500" data-testid="gallery-empty">No {region} images yet. Add some or use AI Generate.</div>
          : Object.entries(byDest).map(([dest, imgs]) => (
            <div key={dest} className="mb-8" data-testid={`dest-group-${dest}`}>
              <div className="mb-3 flex items-center gap-2">
                <h3 className="font-display text-lg font-bold text-navy">{dest}</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${imgs.length >= 15 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{imgs.length} / 15</span>
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {imgs.length === 0 && <div className="col-span-full rounded-xl border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400" data-testid={`empty-dest-${dest}`}>No images yet — add or AI-generate at least 15 for {dest}.</div>}
                {imgs.map((g) => (
                  <div key={g.id} className="group relative overflow-hidden rounded-2xl card-soft">
                    <img src={g.image} alt={g.title} className="h-36 w-full object-cover" />
                    {g.ai && <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-brandorange/90 px-2 py-0.5 text-[10px] font-bold text-white"><Sparkles className="h-2.5 w-2.5" /> AI</span>}
                    <div className="absolute bottom-0 w-full bg-gradient-to-t from-black/70 to-transparent p-3"><p className="text-xs font-semibold text-white">{g.title}</p></div>
                    <button onClick={() => del(g.id)} data-testid={`delete-gallery-${g.id}`} className="absolute right-2 top-2 rounded-lg bg-white/90 p-1.5 text-brandred opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}

      {/* Add image modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined}><DialogHeader><DialogTitle className="font-display text-2xl text-navy">Add Image</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="flex gap-2">
              <button type="button" onClick={() => setAddTab("url")} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${addTab === "url" ? "brand-gradient text-white" : "border border-gray-200 text-navy"}`}>Image URL</button>
              <button type="button" onClick={() => setAddTab("upload")} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${addTab === "upload" ? "brand-gradient text-white" : "border border-gray-200 text-navy"}`}>Upload from Desktop</button>
            </div>
            {addTab === "url" ? <input data-testid="gallery-image" className={input} placeholder="Image URL" value={form.image?.startsWith("data:") ? "" : form.image} onChange={set("image")} />
              : <input type="file" accept="image/*" data-testid="gallery-upload" className={input} onChange={uploadFile} />}
            {form.image && <img src={form.image} alt="" className="h-28 w-full rounded-xl object-cover" />}
            <div className="grid grid-cols-2 gap-2">
              <div><label className={label}>Region</label><select data-testid="gallery-region-select" className={input} value={form.region} onChange={set("region")}>{REGIONS.map((r) => <option key={r}>{r}</option>)}</select></div>
              <div><label className={label}>Destination</label><input data-testid="gallery-destination" className={input} placeholder="Himachal" value={form.destination} onChange={set("destination")} /></div>
            </div>
            <input className={input} placeholder="Title (optional)" value={form.title} onChange={set("title")} />
            <button data-testid="save-gallery-btn" disabled={saving} className="btn-gradient flex w-full items-center justify-center rounded-full py-3 font-bold disabled:opacity-70">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Add"}</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI generate modal */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent aria-describedby={undefined}><DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl text-navy"><Sparkles className="h-6 w-6 text-brandorange" /> AI Image Generator</DialogTitle></DialogHeader>
          <form onSubmit={generate} className="space-y-3">
            <div><label className={label}>Describe the images</label><input data-testid="ai-prompt" className={input} placeholder="e.g. Himachal snow mountains, apple orchards, Manali valley" value={ai.prompt} onChange={setA("prompt")} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className={label}>Count</label><input data-testid="ai-count" type="number" min="1" max="12" className={input} value={ai.count} onChange={setA("count")} /></div>
              <div><label className={label}>Region</label><select data-testid="ai-region" className={input} value={ai.region} onChange={setA("region")}>{REGIONS.map((r) => <option key={r}>{r}</option>)}</select></div>
              <div><label className={label}>Destination</label><input data-testid="ai-destination" className={input} placeholder="Himachal" value={ai.destination} onChange={setA("destination")} /></div>
            </div>
            <p className="text-xs text-gray-400">Images generate in the background and appear automatically (up to 12 per run).</p>
            <button data-testid="ai-generate-submit" disabled={gen} className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3 font-bold disabled:opacity-70">{gen ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Sparkles className="h-5 w-5" /> Generate</>}</button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
